"use client"
/* eslint-disable react-refresh/only-export-components */

import { ColumnDef } from "@tanstack/react-table"
import { Checkbox } from "@/Components/ui/checkbox"
import { Button } from "@/Components/ui/button"
import { MoreHorizontal } from "lucide-react"
import { User } from "./columns"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../../ui/dropdown-menu"
import { useContext, useEffect, useState, useRef } from "react"
import { AppContext } from "@/App"
import { WizardContext } from "@/Components/wizard/wizardProvider"


export type TeamMember = User & {
    projectRole: "Viewer" | "Team Member" | "Sponsor" | "Project Manager"
}

// Component to handle row actions with wizard sendClick support
function TeamMemberActions({ 
    teamMemberId, 
    typeFilter, 
    HandleSetRoleClicked, 
    HandleRemoveClicked 
}: { 
    teamMemberId: string;
    typeFilter?: "Viewer" | "Team Member" | "Sponsor" | "Project Manager";
    HandleSetRoleClicked: (id: string, role: "Viewer" | "Team Member" | "Sponsor" | "Project Manager") => void;
    HandleRemoveClicked: (id: string) => void;
}) {
    const app = useContext(AppContext);
    const wizard = useContext(WizardContext);
    const [openMenu, setOpenMenu] = useState(false);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const wizardControlled = useRef(false);

    useEffect(() => {
        if (wizard.showWizardPointer && wizard.currentStep?.sendClick) {
            const targetButton = wizard.currentStep.useClassSelector 
                ? document.querySelectorAll("." + wizard.currentStep.stepComponentId)[wizard.currentStep.classSelectorNumber || 0]
                : document.getElementById(wizard.currentStep.stepComponentId);

            if (wizard.currentStep.stepComponentId === "table-row-menu-button") {
                if (buttonRef.current && buttonRef.current === targetButton) {
                    wizardControlled.current = true;
                    const timeoutId = setTimeout(() => setOpenMenu(true), 1000);
                    return () => clearTimeout(timeoutId);
                }
            }
        } else {
            wizardControlled.current = false;
        }
    }, [wizard.showWizardPointer, wizard.currentStep]);

    const handleOpenChange = (open: boolean) => {
        if (wizardControlled.current && !open) {
            return;
        }
        setOpenMenu(open);
    };

    return (
        <>
            {app.authLevel > 3 && (
                <DropdownMenu open={openMenu} onOpenChange={handleOpenChange} modal={false}>
                    <DropdownMenuTrigger asChild className="p-0">
                        <Button ref={buttonRef} variant="ghost" className="h-4 w-8 p-0 table-row-menu-button">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        {!typeFilter && (
                            <>
                                <DropdownMenuLabel>Set Role</DropdownMenuLabel>
                                <DropdownMenuItem id="set-role-viewer" onClick={() => HandleSetRoleClicked && HandleSetRoleClicked(teamMemberId, "Viewer")}>
                                    Viewer
                                </DropdownMenuItem>
                                <DropdownMenuItem id="set-role-team-member" onClick={() => HandleSetRoleClicked && HandleSetRoleClicked(teamMemberId, "Team Member")}>
                                    Team Member
                                </DropdownMenuItem>
                                <DropdownMenuItem id="set-role-sponsor" onClick={() => HandleSetRoleClicked && HandleSetRoleClicked(teamMemberId, "Sponsor")}>
                                    Sponsor
                                </DropdownMenuItem>
                                <DropdownMenuItem id="set-role-project-manager" onClick={() => HandleSetRoleClicked && HandleSetRoleClicked(teamMemberId, "Project Manager")}>
                                    Project Manager
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                            </>
                        )}
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuItem id="remove-team-member" onClick={() => HandleRemoveClicked(teamMemberId)}>
                            Remove
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            )}
        </>
    );
}

export function get_team_columns(
    HandleSetRoleClicked: (id: string, role: "Viewer" | "Team Member" | "Sponsor" | "Project Manager") => void, 
    HandleRemoveClicked: (id: string) => void, 
    typeFilter?: "Viewer" | "Team Member" | "Sponsor" | "Project Manager",
    authLevel?: number
): ColumnDef<TeamMember>[] {
    const columns: ColumnDef<TeamMember>[] = [];

    if ((authLevel ?? 0) > 3) {
        columns.push(
            {
                id: "select",
                header: ({ table }) => (
                    <Checkbox
                        id="team-table-select-all"
                        checked={
                            table.getIsAllPageRowsSelected() ||
                            (table.getIsSomePageRowsSelected() && "indeterminate")
                        }
                        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                        aria-label="Select all"
                    />
                ),
                cell: ({ row }) => (
                    <Checkbox
                        checked={row.getIsSelected()}
                        onCheckedChange={(value) => row.toggleSelected(!!value)}
                        aria-label="Select row"
                        className="team-table-row-checkbox"
                    />
                ),
                enableSorting: false,
                enableHiding: false,
            });
    }
    columns.push(
        {
            id: "id",
            accessorKey: "id",
            header: "ID",
            cell: ({ row }) => {
                const id = row.getValue("id");
                return <div className="pl-4">{id as number}</div>
            },
            size: 100,
        },
        {
            id: "forename",
            accessorKey: "forename",
            header: "Forename",
            cell: ({ row }) => {
                const forename = row.getValue("forename");
                return <div className="text-center">{forename as string}</div>
            },
            size: 200,
        },
        {
            id: "surname",
            accessorKey: "surname",
            header: "Surname",
            cell: ({ row }) => {
                const surname = row.getValue("surname");
                return <div className="text-center">{surname as string}</div>
            },
            size: 200,
        },
        {
            id: "displayName",
            accessorKey: "displayName",
            header: "Display Name",
            cell: ({ row }) => {
                const displayName = row.getValue("displayName");
                return <div className="text-center">{displayName as string}</div>
            },
            size: 200,
        },
        {
            id: "email",
            accessorKey: "email",
            header: "Email",
            cell: ({ row }) => {
                const email = row.getValue("email");
                return <div className="text-center">{email as string}</div>
            },
            size: 200,
        });
    //if (!typeFilter) {
        columns.push(
            {
                id: "projectRole",
                accessorKey: "projectRole",
                header: "Role",
                cell: ({ row }) => {
                    const projectRole = row.getValue("projectRole");
                    return <div className="text-center">{projectRole as string}</div>
                },
                size: 200,
            });
   // }
    if ((authLevel ?? 0) > 3) {
        columns.push(
            {
                id: "actions",
                cell: ({ row }) => {
                    return (
                        <TeamMemberActions
                            teamMemberId={row.getValue("id")}
                            typeFilter={typeFilter}
                            HandleSetRoleClicked={HandleSetRoleClicked}
                            HandleRemoveClicked={HandleRemoveClicked}
                        />
                    );
                },
            });
    }
    return columns;
}

export const columnDisplayNames: [string, string][] = [
    ["id", "ID"],
    ["forename", "Forename"],
    ["surname", "Surname"],
    ["displayName", "Display Name"],
    ["email", "Email"],
    ["projectRole", "Role"]
]